import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { nf0 } from "@/lib/format"
import { alsMinSek, alsText, ampelAnfeuchtung, ampelGraedigkeit, type Ampel } from "@/lib/pruefung"
import { antwortDetails, terminalApi, type ProbeAntwort } from "@/lib/terminalApi"

const PUNKT: Record<Exclude<Ampel, null>, string> = { gruen: "#0ca30c", gelb: "#fab219", rot: "#d03b3b" }

function mittel(p: ProbeAntwort, praefix: string): number | null {
  const w = [1, 2, 3].map((i) => p[`${praefix}_${i}`]).filter((x): x is number => typeof x === "number")
  return w.length ? w.reduce((a, b) => a + b, 0) / w.length : null
}

interface Props {
  onZurueck: () => void
  onBearbeiten: (p: ProbeAntwort) => void
  onSitzungWeg: (e: unknown) => boolean
}

/** Letzte Einträge (12 h): eigene lassen sich korrigieren oder stornieren. */
export function Eintraege({ onZurueck, onBearbeiten, onSitzungWeg }: Props) {
  const qc = useQueryClient()
  const [storno, setStorno] = useState<number | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const liste = useQuery({
    queryKey: ["terminal-proben"],
    queryFn: async () => (await terminalApi.get<ProbeAntwort[]>("/proben", { params: { stunden: 12 } })).data,
    retry: false,
  })
  if (liste.error) onSitzungWeg(liste.error)

  async function stornieren(id: number) {
    try {
      await terminalApi.post(`/proben/${id}/storno`)
      setStorno(null)
      void qc.invalidateQueries({ queryKey: ["terminal-proben"] })
      void qc.invalidateQueries({ queryKey: ["terminal-vorauswahl"] })
    } catch (e) {
      if (!onSitzungWeg(e)) setFehler(antwortDetails(e).detail)
    }
  }

  const ampelPunkt = (a: Ampel, titel: string) =>
    a ? <span title={titel} className="inline-block h-3 w-3 rounded-full align-middle" style={{ background: PUNKT[a] }} /> : null

  return (
    <div className="grid max-w-6xl gap-4">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-2xl font-semibold">Letzte Einträge (12 Stunden)</h2>
        <button type="button" onClick={onZurueck} className="h-12 rounded-xl border-2 border-hair/10 bg-surface px-5 text-base">Zurück</button>
      </div>
      {fehler && <p role="alert" className="rounded-xl bg-crit/10 p-4">{fehler}</p>}
      {!liste.data ? (
        <p className="text-lg text-ink2">Wird geladen …</p>
      ) : liste.data.length === 0 ? (
        <p className="text-lg text-ink2">In den letzten 12 Stunden wurde noch nichts erfasst.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border-2 border-hair/10 bg-surface">
          <table className="tabular w-full border-collapse text-base">
            <thead>
              <tr className="text-left text-sm text-ink2">
                {["Fenster", "Mischung", "NC-Typ", "Anfeuchtung", "Grädigkeit", "Zyklus", "Auswurf", "Durchsatz", "Von", ""].map((h) => (
                  <th key={h} className="px-3 py-2 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {liste.data.map((p) => {
                const anf = mittel(p, "anf_ist")
                const gr = mittel(p, "graed_ist")
                return (
                  <tr key={p.id} className="border-t border-grid">
                    <td className="px-3 py-3">{`${new Date(p.datum).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} ${p.zeitfenster ?? ""}`}</td>
                    <td className="px-3 py-3 font-mono">{p.mischung}</td>
                    <td className="px-3 py-3 font-mono">{p.nc_typ}</td>
                    <td className="px-3 py-3">{ampelPunkt(ampelAnfeuchtung(anf, p.anf_soll as number | null), "Anfeuchtung")} {anf != null ? alsText(Math.round(anf * 10) / 10) : "–"}</td>
                    <td className="px-3 py-3">{ampelPunkt(ampelGraedigkeit(gr, p.graed_soll as number | null), "Grädigkeit")} {gr != null ? alsText(Math.round(gr * 10) / 10) : "–"}</td>
                    <td className="px-3 py-3">{alsMinSek(p.zykluszeit_s as number | null)}</td>
                    <td className="px-3 py-3">{p.auswurfgewicht_kg != null ? `${alsText(p.auswurfgewicht_kg as number)} kg` : "–"}</td>
                    <td className="px-3 py-3">{p.durchsatz_kg_h != null ? `${nf0.format(p.durchsatz_kg_h)} kg/h` : "–"}</td>
                    <td className="px-3 py-3 text-ink2">{p.erfasst_von ?? (p.quelle === "terminal" ? "" : "Excel")}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {p.eigene && (storno === p.id ? (
                        <span className="inline-flex gap-2">
                          <button type="button" onClick={() => void stornieren(p.id)} className="h-11 rounded-xl bg-crit px-4 text-white">Wirklich stornieren</button>
                          <button type="button" onClick={() => setStorno(null)} className="h-11 rounded-xl px-3">Nein</button>
                        </span>
                      ) : (
                        <span className="inline-flex gap-2">
                          <button type="button" onClick={() => onBearbeiten(p)} className="h-11 rounded-xl border-2 border-hair/10 px-4">Ändern</button>
                          <button type="button" onClick={() => setStorno(p.id)} className="h-11 rounded-xl px-3 text-crit">Stornieren</button>
                        </span>
                      ))}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
